import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, Square, Volume2, VolumeX, Maximize2, Loader } from 'lucide-react';

const VIDEO_URL = 'https://customer-assets.emergentagent.com/job_nostalgic-desktop-1/artifacts/0xd7pf3y_Lexy%20CTO%205.mp4';

export default function VideoPlayer() {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false); // Don't start muted - user controls volume
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [isLoading, setIsLoading] = useState(false); // Don't show loading until user clicks play
  const [canPlay, setCanPlay] = useState(false);
  const [hasStarted, setHasStarted] = useState(false); // Track if user has clicked play

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => setCurrentTime(video.currentTime);
    const handleLoadedMetadata = () => {
      setDuration(video.duration);
      setCanPlay(true);
      setIsLoading(false);
    };
    const handleCanPlay = () => {
      setIsLoading(false);
      setCanPlay(true);
      // Don't autoplay - wait for user to click play
    };
    const handleWaiting = () => {
      if (hasStarted) setIsLoading(true);
    };
    const handlePlaying = () => setIsLoading(false);
    const handleEnded = () => setIsPlaying(false);
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('waiting', handleWaiting);
    video.addEventListener('playing', handlePlaying);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('waiting', handleWaiting);
      video.removeEventListener('playing', handlePlaying);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
    };
  }, [hasStarted]);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        setHasStarted(true);
        setIsLoading(true);
        videoRef.current.play().catch(() => {
          setIsLoading(false);
        });
      }
    }
  };

  const handleStop = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    if (videoRef.current) {
      videoRef.current.currentTime = pos * duration;
    }
  };

  const handleVolumeChange = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    const newVolume = Math.max(0, Math.min(1, pos));
    setVolume(newVolume);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
    }
  };

  const toggleFullscreen = () => {
    if (videoRef.current) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen();
      }
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div style={{
      background: '#000',
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      fontFamily: "'Segoe UI', Tahoma, sans-serif"
    }}>
      {/* Menu Bar */}
      <div style={{
        background: '#c0c0c0',
        padding: '2px 4px',
        borderBottom: '1px solid #808080',
        fontSize: '12px',
        display: 'flex',
        gap: '12px'
      }}>
        <span style={{ cursor: 'pointer' }}>File</span>
        <span style={{ cursor: 'pointer' }}>View</span>
        <span style={{ cursor: 'pointer' }}>Play</span>
        <span style={{ cursor: 'pointer' }}>Help</span>
      </div>

      {/* Video Container */}
      <div style={{
        flex: 1,
        background: '#000',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <video
          ref={videoRef}
          src={VIDEO_URL}
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain'
          }}
          onClick={togglePlay}
          playsInline
          preload="metadata"
          muted={isMuted}
          poster=""
        />
        
        {/* Loading/Play overlay */}
        {(!isPlaying || isLoading) && (
          <div 
            onClick={togglePlay}
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              background: 'rgba(0,0,0,0.5)'
            }}
          >
            <div style={{
              width: '70px',
              height: '70px',
              background: 'rgba(0,0,128,0.95)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '3px solid white',
              boxShadow: '0 4px 12px rgba(0,0,0,0.4)'
            }}>
              {isLoading && hasStarted ? (
                <Loader size={28} color="white" style={{ animation: 'spin 1s linear infinite' }} />
              ) : (
                <Play size={32} color="white" fill="white" style={{ marginLeft: '4px' }} />
              )}
            </div>
            {!hasStarted && (
              <div style={{
                marginTop: '12px',
                color: 'white',
                fontSize: '12px',
                fontFamily: "'Segoe UI', sans-serif",
                textShadow: '0 1px 3px rgba(0,0,0,0.5)'
              }}>
                Click to Play
              </div>
            )}
            {isLoading && hasStarted && (
              <div style={{
                marginTop: '12px',
                color: 'white',
                fontSize: '12px',
                fontFamily: "'Segoe UI', sans-serif",
                textShadow: '0 1px 3px rgba(0,0,0,0.5)'
              }}>
                Loading video...
              </div>
            )}
          </div>
        )}
      </div>

      {/* Progress Bar */}
      <div 
        onClick={handleSeek}
        style={{
          height: '16px',
          background: '#c0c0c0',
          borderTop: '2px solid',
          borderColor: '#808080 #fff #fff #808080',
          cursor: 'pointer',
          padding: '4px'
        }}
      >
        <div style={{
          height: '100%',
          background: '#fff',
          border: '1px solid #808080',
          position: 'relative'
        }}>
          <div style={{
            height: '100%',
            width: `${duration ? (currentTime / duration) * 100 : 0}%`,
            background: '#000080'
          }} />
        </div>
      </div>

      {/* Controls */}
      <div style={{
        background: '#c0c0c0',
        padding: '6px 8px',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        borderTop: '1px solid #fff'
      }}>
        {/* Play/Pause */}
        <button
          onClick={togglePlay}
          style={{
            width: '28px',
            height: '24px',
            background: '#c0c0c0',
            border: '2px solid',
            borderColor: '#fff #404040 #404040 #fff',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {isPlaying ? <Pause size={12} /> : <Play size={12} />}
        </button>

        {/* Stop */}
        <button
          onClick={handleStop}
          style={{
            width: '28px',
            height: '24px',
            background: '#c0c0c0',
            border: '2px solid',
            borderColor: '#fff #404040 #404040 #fff',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Square size={10} fill="#000" />
        </button>

        {/* Spacer */}
        <div style={{ width: '8px' }} />

        {/* Mute */}
        <button
          onClick={toggleMute}
          style={{
            width: '28px',
            height: '24px',
            background: '#c0c0c0',
            border: '2px solid',
            borderColor: '#fff #404040 #404040 #fff',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {isMuted ? <VolumeX size={12} /> : <Volume2 size={12} />}
        </button>

        {/* Volume Slider */}
        <div 
          onClick={handleVolumeChange}
          style={{
            width: '60px',
            height: '12px',
            background: '#fff',
            border: '1px solid #808080',
            cursor: 'pointer',
            position: 'relative'
          }}
        >
          <div style={{
            height: '100%',
            width: `${volume * 100}%`,
            background: '#000080'
          }} />
        </div>

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* Time */}
        <div style={{
          fontSize: '11px',
          fontFamily: 'monospace',
          color: '#000',
          background: '#fff',
          padding: '2px 6px',
          border: '1px solid #808080'
        }}>
          {formatTime(currentTime)} / {formatTime(duration)}
        </div>

        {/* Fullscreen */}
        <button
          onClick={toggleFullscreen}
          style={{
            width: '28px',
            height: '24px',
            background: '#c0c0c0',
            border: '2px solid',
            borderColor: '#fff #404040 #404040 #fff',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Maximize2 size={12} />
        </button>
      </div>

      {/* Status Bar */}
      <div style={{
        background: '#c0c0c0',
        padding: '2px 6px',
        fontSize: '11px',
        borderTop: '1px solid #808080',
        display: 'flex',
        justifyContent: 'space-between'
      }}>
        <span>Lexy CTO 5.mp4</span>
        <span>
          {!hasStarted ? 'Ready' : 
           isLoading ? 'Buffering...' : 
           isPlaying ? 'Playing' : 'Paused'}
        </span>
      </div>
    </div>
  );
}
